import { Component, DestroyRef, OnInit, computed, inject, input, output, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormControl, ReactiveFormsModule, Validators } from "@angular/forms";
import { TranslatePipe } from "@ngx-translate/core";
import { KENDO_BUTTON } from "@progress/kendo-angular-buttons";
import { KENDO_DIALOG } from "@progress/kendo-angular-dialog";
import { KENDO_TEXTAREA } from "@progress/kendo-angular-inputs";
import { KENDO_TABSTRIP } from "@progress/kendo-angular-layout";
import { v7 as uuidv7 } from "uuid";
import { BootstrapService } from "../../shared/bootstrap/bootstrap.service";
import { Flights2Service } from "../flights2.service";
import { EditOption } from "../models/edit-option.model";
import { Flight } from "../models/flight.model";
import { FlightEditForm, buildFlightEditForm } from "./form/flight-edit-form";
import { FlightEditLookups } from "./form/flight-edit-lookups";
import { quantityOf, unitOf, unitsWith } from "./form/quantities";
import { accessoryLabel } from "./form/names";
import { OIL_CIRCUITS, oilCircuitsOf } from "./tabs/oil/oil-group";
import { FlightEditSnapshot, countOf, snapshotOf } from "./request/diff";
import { buildEditRequest } from "./request/edit-request";
import { CrewTabComponent } from "./tabs/crew/crew-tab.component";
import { EngineDataTabComponent } from "./tabs/engine-data/engine-data-tab.component";
import { EnginePowerCheckTabComponent } from "./tabs/engine-power-check/engine-power-check-tab.component";
import { FlightTabComponent } from "./tabs/flight/flight-tab.component";
import { HoistTabComponent } from "./tabs/hoist/hoist-tab.component";
import { HookTabComponent } from "./tabs/hook/hook-tab.component";
import { OilTabComponent } from "./tabs/oil/oil-tab.component";
import { PenaltyFactorTabComponent } from "./tabs/penalty-factor/penalty-factor-tab.component";
import { RefuelTabComponent } from "./tabs/refuel/refuel-tab.component";

/**
 * The dialog that edits a flight: it holds the form, built from one file per part of the flight, and shows a tab for
 * each. It sends only what the user changed.
 */
@Component({
    selector: "app-flight-edit-dialog",
    imports: [
        ReactiveFormsModule,
        TranslatePipe,
        KENDO_DIALOG,
        KENDO_TABSTRIP,
        KENDO_BUTTON,
        KENDO_TEXTAREA,
        FlightTabComponent,
        EngineDataTabComponent,
        EnginePowerCheckTabComponent,
        PenaltyFactorTabComponent,
        RefuelTabComponent,
        OilTabComponent,
        HookTabComponent,
        HoistTabComponent,
        CrewTabComponent,
    ],
    templateUrl: "./flight-edit-dialog.component.html",
    styleUrl: "./flight-edit-dialog.component.scss",
})
export class FlightEditDialogComponent implements OnInit {
    private readonly flights = inject(Flights2Service);
    private readonly bootstrap = inject(BootstrapService);
    private readonly destroyRef = inject(DestroyRef);

    readonly flight = input.required<Flight>();
    readonly saved = output<void>();
    readonly closed = output<void>();

    /** Each hook and hoist by its serial and name, or whichever of the two the logbook has. */
    readonly hookLabels = computed(() => this.flight().hooks.map((hook) => accessoryLabel(hook)));
    readonly hoistLabels = computed(() => this.flight().hoists.map((hoist) => accessoryLabel(hoist)));

    /** What the organization's configurations turn on: which tabs and forms the dialog shows. */
    readonly configs = this.bootstrap.configs;

    /** The oil circuits the organization logs. */
    readonly oilCircuits = computed(() => oilCircuitsOf(this.configs()));

    private readonly aircraft = computed(() => this.bootstrap.aircraftById().get(this.flight().helicopterId ?? 0));

    /** A single engine aircraft logs no second engine: its fields stay out of the way. */
    readonly engines = computed(() => (this.aircraft()?.hasEngine2 ? [1, 2] : [1]) as (1 | 2)[]);

    /** Built from the lists the bootstrap holds, so a value they preselect never reads as a change. */
    readonly ready = signal(false);
    readonly changedCount = signal(0);
    readonly saving = signal(false);
    readonly failed = signal(false);

    readonly reason = new FormControl("", { nonNullable: true, validators: [Validators.required, Validators.pattern(/\S/)] });

    form!: FlightEditForm;
    lookups!: FlightEditLookups;
    crewMembers: EditOption[] = [];

    /** One per edit, so that sending it again after a failure is not saved twice. */
    private readonly idempotencyKey = uuidv7();

    /** The values as the dialog opened, to send only what the user changed. */
    private initial!: FlightEditSnapshot;

    ngOnInit(): void {
        this.lookups = this.lookupsOf();
        this.crewMembers = this.lookups.crewMembersOf(this.flight());
        this.form = buildFlightEditForm(this.flight(), this.lookups, this.destroyRef);
        this.initial = snapshotOf(this.form);
        this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.refresh());
        this.refresh();
        this.ready.set(true);
    }

    save(): void {
        if (this.form.invalid || this.reason.invalid || !this.changedCount()) return;
        this.saving.set(true);
        this.failed.set(false);
        this.flights.modify(this.requestOf()).subscribe({
            next: () => {
                this.saving.set(false);
                this.saved.emit();
            },
            error: () => {
                this.saving.set(false);
                this.failed.set(true);
            },
        });
    }

    private refresh(): void {
        this.changedCount.set(countOf(this.requestOf().htlFlight));
    }

    private requestOf() {
        return buildEditRequest({
            flight: this.flight(),
            form: this.form,
            initial: this.initial,
            lookups: this.lookups,
            reason: this.reason.value,
            idempotencyKey: this.idempotencyKey,
        });
    }

    /** Everything the dialog picks from comes with the organization's bootstrap, already kept by the page. */
    private lookupsOf(): FlightEditLookups {
        const flight = this.flight();
        const bootstrap = this.bootstrap.bootstrap();
        return new FlightEditLookups({
            pilots: this.bootstrap.pilots().map((pilot) => ({ id: pilot.id, name: [pilot.firstName, pilot.lastName].filter((part) => !!part?.trim()).join(" ") })),
            flightTypes: this.bootstrap.flightTypes().map((type) => ({ id: type.id, name: type.name })),
            pilotRoles: this.bootstrap.pilotRoles(),
            crewRoles: this.bootstrap.crewRoles(),
            airports: bootstrap?.airports ?? [],
            crews: this.bootstrap.crews(),
            flightOperations: bootstrap?.flightOperations ?? [],
            flightSpecifications: bootstrap?.flightSpecifications ?? [],
            flightTypeNotes: bootstrap?.flightTypeNotes ?? [],
            // Besides the organization's units, any unit the flight's own fuel and refuels already have, so a value keeps its own.
            fuelUnits: unitsWith(
                this.bootstrap.fuelUnits(),
                [
                    flight.fuelArrival,
                    flight.fuelPlanned,
                    flight.refuel,
                    flight.totalFuel,
                    ...flight.refuelBeforeFirstFlight.flatMap((refuel) => [refuel.remainingFuel, refuel.fuelPlanned, refuel.refuel, refuel.totalFuel]),
                ].map((text) => quantityOf(text).unit),
            ),
            oilUnits: unitsWith(
                this.bootstrap.oilUnits(),
                flight.oilSuppliedBeforeFlight.flatMap((supply) => OIL_CIRCUITS.map((circuit) => unitOf(supply[circuit.key]?.unit ?? null))),
            ),
            flight: flight,
            configs: this.configs(),
            maxTank: this.bootstrap.maxTankCapacityEnabled() ? (this.aircraft()?.maxTankCapacity ?? null) : null,
            abbreviationKey: (abbreviation) => this.bootstrap.abbreviationKey(abbreviation),
        });
    }
}
